import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { cn } from "@/lib/utils";
import { Avatar, Spinner } from "@fluentui/react-components";
import { LayoutList, MapPin, Search, SquareKanban } from "lucide-react";

import TeamTable from "./team-table";
import JobTable from "../../job-pipeline/job-table";

import { useAdminCompanyDetailsQuery } from "@/queries/admin/company-details";
import { AddOpportunityDialog } from "@/components/dialogs/company/opportunity/add-opportunity";
import { AdminPipelineJob } from "@/queries/admin/job-pipeline";
import JobKanban from "../../job-pipeline/job-kanban";

const CompanyDetails = () => {
  const [activeTab, setActiveTab] = useState<"team" | "jobs">("team");
  const [view, setView] = useState<"kanban" | "table">("kanban");
  const [searchString, setSearchString] = useState("");
  const [addOpportunityDialogOpen, setAddOpportunityDialogOpen] =
    useState(false);

  const navigate = useNavigate();

  const { companyId } = useParams();

  const { adminCompanyDetails: company, adminCompanyDetailsLoading } =
    useAdminCompanyDetailsQuery(companyId);

  const teamMembers = useMemo(() => {
    if (!company || activeTab !== "team") {
      return [];
    } else if (!searchString) {
      return company.team;
    } else {
      const filterString = searchString.toLowerCase();

      return company?.team?.filter((user) =>
        user.name?.toLowerCase().includes(filterString),
      );
    }
  }, [company, searchString, activeTab]);

  const jobs = useMemo(() => {
    if (!company || activeTab !== "jobs") {
      return [];
    } else if (!searchString) {
      return company.jobs;
    } else {
      const filterString = searchString.toLocaleLowerCase();

      /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
      return company.jobs.filter(
        (job) =>
          job.role?.toLocaleLowerCase().includes(filterString) ||
          job.company.name?.toLocaleLowerCase().includes(filterString) ||
          job.status.toLocaleLowerCase().includes(filterString),
      );
      /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */
    }
  }, [company, searchString, activeTab]);

  const setSelectedJob = useCallback(
    (job?: AdminPipelineJob) => job && navigate(`/new-admin/job/${job.id}`),
    [navigate],
  );

  const toggleTab = useCallback(
    (tab: "team" | "jobs") => {
      let changed = false;
      setActiveTab((prev) => {
        if (prev !== tab) {
          changed = true;
        }

        return tab;
      });

      if (changed) {
        setActiveTab(tab);
        setView("kanban");
        setSearchString("");
      }
    },
    [setActiveTab, setSearchString],
  );

  if (adminCompanyDetailsLoading || !company) {
    return (
      <div>
        <div className="w-full flex-col justify-around align-middle">
          <Spinner size="huge" />
          <p className="text-center text-base font-medium text-[#727E94]">
            Loading...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="py-4 w-full flex flex-col gap-4 items-start self-stretch">
        <div className="pl-3 flex flex-col self-stretch justify-center">
          <div className="flex self-stretch items-center justify-between gap-2">
            <div className="flex self-stretch items-center gap-2">
              <Avatar
                image={{ src: company.logoLink }}
                name={company.name}
                className="h-[44px] w-[44px]"
                size={48}
              />
              <div className="flex flex-col gap-0.5">
                <p className="text-lg text-[#181D27] font-semibold leading-6">
                  {company.name}
                </p>
                {Boolean(company.location) && (
                  <div className="flex items-center gap-1">
                    <MapPin stroke="#727e94" size={16} />
                    <p className="text-sm font-medium text-[#727E94]">
                      {company.location}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="md:pl-14 flex flex-col">
            <div className="flex md:flex-row md:items-center self-stretch gap-3">
              <a
                href={`/company/${company.id}/edit`}
                target="_blank"
                rel="noreferrer"
                className="text-s text-[#646cff] font-medium hover:underline"
              >
                Profile
              </a>
              {Boolean(company.linkedin) && (
                <a
                  href={company.linkedin}
                  target="_blank"
                  rel="noreferrer"
                  className="text-s text-[#646cff] font-medium hover:underline"
                >
                  LinkedIn
                </a>
              )}
              {Boolean(company.websiteLink) && (
                <a
                  href={company.websiteLink}
                  target="_blank"
                  rel="noreferrer"
                  className="text-s text-[#646cff] font-medium hover:underline"
                >
                  Website
                </a>
              )}
              {Boolean(company.attioId) && (
                <a
                  href={`https://app.attio.com/venturous/company/${company.attioId}/activity`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-s text-[#646cff] font-medium hover:underline"
                >
                  Attio
                </a>
              )}
            </div>
            <div>
              <AddOpportunityDialog
                open={addOpportunityDialogOpen}
                setOpen={setAddOpportunityDialogOpen}
                includeMaskedJobDescription
                companyId={company.id}
              />
            </div>
          </div>
        </div>
        <div className="flex flex-col md:flex-row md:items-center justify-between self-stretch">
          <div className="flex space-x-4 border-b border-gray-200">
            <button
              onClick={() => toggleTab("team")}
              className={cn(
                "pb-2 px-3 text-sm font-medium rounded-none !outline-0 border-0",
                activeTab === "team"
                  ? "border-b-2 border-black text-black"
                  : "text-gray-500 hover:text-black",
              )}
            >
              Team Members ({company.team.length})
            </button>
            <button
              onClick={() => toggleTab("jobs")}
              className={cn(
                "pb-2 px-3 text-sm font-medium rounded-none !outline-0 border-0",
                activeTab === "jobs"
                  ? "border-b-2 border-black text-black"
                  : "text-gray-500 hover:text-black",
              )}
            >
              Jobs ({company.jobs.length})
            </button>
          </div>
          <div className="flex flex-row items-center gap-3">
            <div
              className={cn(
                "flex flex-row items-center gap-0.5",
                "border-b border-[#57576B] transition-colors focus-within:border-black",
              )}
            >
              <Search />
              <input
                type="text"
                className={cn(
                  "flex h-10 w-full min-w-48 px-3 py-2 text-base",
                  "file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
                )}
                placeholder={
                  activeTab === "team"
                    ? "Search for any team member"
                    : "Search for any job"
                }
                value={searchString}
                onChange={(ev) => {
                  ev.preventDefault();
                  setSearchString(ev.target.value);
                }}
              />
            </div>
            {activeTab === "jobs" && (
              <div className="flex flex-row items-start bg-[#F9F9FB] rounded-lg">
                <div
                  className={cn(
                    "h-10 w-10 flex items-center justify-center rounded-[10px]",
                    "cursor-pointer hover:bg-white hover:border hover:border-[#E5E5EC] hover:shadow-[0px_1px_2px_0px_rgba(82,88,102,0.06)]",
                    view === "kanban" &&
                      "bg-white border border-[#E5E5EC] shadow-[0px_1px_2px_0px_rgba(82,88,102,0.06)]",
                  )}
                  onClick={() => setView("kanban")}
                >
                  <SquareKanban />
                </div>
                <div
                  className={cn(
                    "h-10 w-10 flex items-center justify-center rounded-[10px]",
                    "cursor-pointer hover:bg-white hover:border hover:border-[#E5E5EC] hover:shadow-[0px_1px_2px_0px_rgba(82,88,102,0.06)]",
                    view === "table" &&
                      "bg-white border border-[#E5E5EC] shadow-[0px_1px_2px_0px_rgba(82,88,102,0.06)]",
                  )}
                  onClick={() => setView("table")}
                >
                  <LayoutList />
                </div>
              </div>
            )}
          </div>
        </div>
        {activeTab === "team" ? (
          <TeamTable companyTeam={teamMembers} companyId={company.id} />
        ) : view === "kanban" ? (
          <JobKanban jobs={jobs} selectJob={setSelectedJob} />
        ) : (
          <JobTable jobs={jobs} selectJob={setSelectedJob} omitCompany />
        )}
      </div>
    </div>
  );
};

export default CompanyDetails;
